import {
	VIEW_PAGE_FEATURES,
	DEFAULT_ALLOWED_FEATURES,
} from '@atlassian/embedded-confluence/features';

import { useAllowedFeatures } from '../common/useAllowedFeatures.ts';
import type { FeatureMode } from '../common/useAllowedFeatures.ts';

const { view: VIEW_PAGE_DEFAULT_ALLOWED_FEATURES } = DEFAULT_ALLOWED_FEATURES;

type LoomExternalOpenConfig = { 'loom-external-open': { route: string } };

export const useEmbeddedViewPageFeatures = () => {
	const allowedFeatures = useAllowedFeatures<FeatureMode.VIEW>();

	/**
	 * When allowedFeatures === all, we don't want to enable features that are suppose to disable features.
	 * For example, we want to keep "disable-share" out of allowedViewPageFeatures,
	 * so that the Share button won't be disable when allowedFeatures is "all".
	 */
	const disableFeatures = [
		'disable-share',
		'modern-header',
		'disable-help-button',
		'disable-title-toolbar',
	];

	const allowedViewPageFeatures =
		allowedFeatures === 'all'
			? VIEW_PAGE_FEATURES.filter((feature) => !disableFeatures.includes(feature))
			: allowedFeatures;

	const showShare = !(
		allowedViewPageFeatures?.includes('disable-share') ??
		VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('disable-share')
	);

	return {
		showShare,
		hasByLineContributors:
			allowedViewPageFeatures?.includes('byline-contributors') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('byline-contributors'),
		hasByLineExtensions:
			allowedViewPageFeatures?.includes('byline-extensions') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('byline-extensions'),
		hasPageReactions:
			allowedViewPageFeatures?.includes('page-reactions') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('page-reactions'),
		hasStickyHeader:
			allowedViewPageFeatures?.includes('sticky-header') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('sticky-header'),
		showEdit:
			allowedViewPageFeatures?.includes('edit') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('edit'),
		showDelete:
			allowedViewPageFeatures?.includes('delete') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('delete'),
		hasComments:
			allowedViewPageFeatures?.includes('page-comments') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('page-comments'),
		hasInlineCommentsAndHighlightActions:
			allowedViewPageFeatures?.includes('inline-comments') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('inline-comments'),
		includeNonLicensedUsersToShare:
			showShare &&
			(allowedViewPageFeatures?.includes('non-licensed-share') ??
				VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('non-licensed-share')),
		hasModernHeader:
			allowedViewPageFeatures?.includes('modern-header') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('modern-header'),
		hideHelpButton:
			allowedViewPageFeatures?.includes('disable-help-button') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('disable-help-button'),
		hideTitleToolbar:
			allowedViewPageFeatures?.includes('disable-title-toolbar') ??
			VIEW_PAGE_DEFAULT_ALLOWED_FEATURES.includes('disable-title-toolbar'),
		loomExternalOpenRoute: (allowedViewPageFeatures as Array<unknown>)?.find(
			(x): x is LoomExternalOpenConfig =>
				typeof x === 'object' && x !== null && 'loom-external-open' in x,
		)?.['loom-external-open']?.route,
	};
};
